'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';

export type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
};

type ConfirmState = ConfirmOptions & {
  resolve: (value: boolean) => void;
};

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null);

function ConfirmDialogPanel({
  state,
  onClose,
}: {
  state: ConfirmState | null;
  onClose: (value: boolean) => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!state) return;
    document.body.style.overflow = 'hidden';
    cancelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [state, onClose]);

  const isDanger = state?.variant === 'danger';

  return (
    <AnimatePresence>
      {state && (
        <>
          <motion.button
            type="button"
            aria-label="Close dialog"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => onClose(false)}
            className="fixed inset-0 z-[100] bg-[#2d3536]/70 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="confirm-dialog-title"
              aria-describedby="confirm-dialog-message"
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="pointer-events-auto w-full max-w-md overflow-hidden rounded-3xl border border-[#E6E2D7] bg-[#FAF8F2] shadow-[0_24px_60px_-20px_rgba(45,53,54,0.35)]"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="bg-[#2D3536] px-6 py-5 border-b border-[#3D4748]">
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
                      isDanger ? 'bg-[#a65d5d]/20 text-[#f2b8b8]' : 'bg-[#5F6F52]/30 text-[#98AA9D]'
                    }`}
                  >
                    <AlertTriangle className="h-5 w-5" aria-hidden />
                  </span>
                  <div className="min-w-0 pt-1">
                    <h2
                      id="confirm-dialog-title"
                      className="font-display text-lg font-bold text-[#FAF8F2]"
                    >
                      {state.title ?? 'Confirm Action'}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="px-6 py-6 bg-[#FAF8F2]">
                <p id="confirm-dialog-message" className="text-sm leading-relaxed text-[#2D3536]/80 font-medium">
                  {state.message}
                </p>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button ref={cancelRef} variant="secondary" onClick={() => onClose(false)} className="rounded-xl font-semibold">
                    {state.cancelLabel ?? 'Cancel'}
                  </Button>
                  <Button
                    variant={isDanger ? 'destructive' : 'primary'}
                    onClick={() => onClose(true)}
                    className={`rounded-xl font-bold ${
                      isDanger ? 'bg-[#a65d5d] hover:bg-[#8f4f4f] text-white' : 'bg-[#5F6F52] hover:bg-[#4E5C43] text-white'
                    }`}
                  >
                    {state.confirmLabel ?? 'Confirm'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmState | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ ...options, resolve });
    });
  }, []);

  const close = useCallback((value: boolean) => {
    setState((current) => {
      current?.resolve(value);
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialogPanel state={state} onClose={close} />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    return async (options: ConfirmOptions) => {
      if (typeof window === 'undefined') return false;
      return window.confirm(options.message);
    };
  }
  return confirm;
}
